import React, { useRef, useEffect } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { UIPerformance } from '../core/UIConfig';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}

export const Slider = ({ label, value, min, max, step, onChange }: SliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const isDragging = useRef(false);

  const valueToX = (v: number) => {
    if (!trackRef.current) return 0;
    const trackWidth = trackRef.current.offsetWidth;
    return ((v - min) / (max - min)) * trackWidth;
  };

  useEffect(() => {
    x.set(valueToX(value));
  }, [value, min, max, trackRef.current]);

  useEffect(() => {
    const unsubscribe = x.on("change", (latestX) => {
        const trackWidth = trackRef.current?.offsetWidth ?? 0;
        if (trackWidth === 0) return;

        const proportion = latestX / trackWidth;
        const latestValue = min + proportion * (max - min);
        const steppedValue = Math.round(latestValue / step) * step;
        onChange(steppedValue);
    });
    return unsubscribe;
  }, [x, min, max, step, onChange]);

  // Add global mouse up listener to ensure drag state is always released
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = 'default';
      }
    };

    const handleGlobalPointerUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = 'default';
      }
    };

    // Also handle pointer leave to catch edge cases
    const handleGlobalPointerLeave = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = 'default';
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp, true); // Use capture phase
    document.addEventListener('pointerup', handleGlobalPointerUp, true); // Use capture phase
    document.addEventListener('pointerleave', handleGlobalPointerLeave, true);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp, true);
      document.removeEventListener('pointerup', handleGlobalPointerUp, true);
      document.removeEventListener('pointerleave', handleGlobalPointerLeave, true);
    };
  }, []);

  const percentage = ((value - min) / (max - min)) * 100;
  
  // Check performance configuration
  const config = UIPerformance.getConfig();

  return (
    <div style={styles.container} data-slider-element="container">
      <div style={styles.labelContainer}>
        <label style={styles.label}>{label}</label>
        <span style={config.disableGlowEffects ? styles.valueNoGlow : styles.value}>
          {value.toFixed(2)}
        </span>
      </div>
      <motion.div
        ref={trackRef}
        style={styles.track}
        data-slider-element="track"
        onPointerDown={(event) => {
            if (!trackRef.current) return;
            const trackRect = trackRef.current.getBoundingClientRect();
            const clickX = event.clientX - trackRect.left;
            x.set(clickX);
        }}
      >
        {/* Cosmic glow effect - conditionally rendered for performance */}
        {!config.disableGlowEffects && (
          <div style={{
            ...styles.cosmicGlow,
            width: `${percentage}%`,
          }} />
        )}
        
        <motion.div 
          style={{
            ...styles.progress, 
            width: x,
            background: `linear-gradient(90deg, 
              rgba(147, 112, 219, 0.8) 0%, 
              rgba(138, 43, 226, 0.9) 50%, 
              rgba(75, 0, 130, 1) 100%
            )`,
            boxShadow: config.disableGlowEffects ? 
              'inset 0 1px 0 rgba(255, 255, 255, 0.3)' :
              `
                0 0 20px rgba(147, 112, 219, 0.5),
                0 0 40px rgba(147, 112, 219, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.3)
              `,
          }} 
          data-slider-element="progress"
        />
        
        <motion.div
          style={{ ...styles.thumbContainer, x, y: '-50%' }}
          drag="x"
          dragConstraints={trackRef}
          dragElastic={0}
          dragMomentum={false}
          dragTransition={{ bounceStiffness: 300, bounceDamping: 40 }}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9, cursor: 'grabbing' }}
          data-slider-element="thumb"
          onPointerDown={(event) => {
            // Prevent track click when clicking on thumb
            event.stopPropagation();
            isDragging.current = true;
          }}
          onPointerUp={() => {
            // Ensure drag state is released on pointer up
            isDragging.current = false;
            document.body.style.cursor = 'default';
          }}
          onPointerLeave={() => {
            // Release drag state if pointer leaves the thumb while dragging
            if (isDragging.current) {
              isDragging.current = false;
              document.body.style.cursor = 'default';
            }
          }}
          onDragStart={() => {
            isDragging.current = true;
          }}
          onDragEnd={() => {
            // Force release any stuck drag state
            isDragging.current = false;
            document.body.style.cursor = 'default';
          }}
        >
          <motion.div 
            style={config.disableGlowEffects ? styles.thumbNoGlow : styles.thumb}
            data-slider-element="thumb-visual"
            whileHover={config.disableGlowEffects ? {} : {
              boxShadow: `
                0 0 30px rgba(147, 112, 219, 0.8),
                0 0 60px rgba(147, 112, 219, 0.4),
                0 4px 20px rgba(0, 0, 0, 0.3)
              `
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        width: '100%',
        padding: '12px 0',
        marginBottom: '8px',
    },
    labelContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        fontSize: '14px',
        fontWeight: '500',
    },
    label: {
        color: 'rgba(255, 255, 255, 0.9)',
        letterSpacing: '0.3px',
        textShadow: '0 0 10px rgba(255, 255, 255, 0.2)',
    },
    value: {
        color: 'rgba(147, 112, 219, 0.9)',
        fontWeight: '600',
        fontSize: '13px',
        textShadow: '0 0 15px rgba(147, 112, 219, 0.5)',
        fontFamily: 'monospace',
        background: 'rgba(147, 112, 219, 0.1)',
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid rgba(147, 112, 219, 0.3)',
    },
    valueNoGlow: {
        color: 'rgba(147, 112, 219, 0.9)',
        fontWeight: '600',
        fontSize: '13px',
        fontFamily: 'monospace',
        background: 'rgba(147, 112, 219, 0.1)',
        padding: '4px 8px',
        borderRadius: '6px',
        border: '1px solid rgba(147, 112, 219, 0.3)',
        // No textShadow for performance
    },
    track: {
        position: 'relative',
        width: '100%',
        height: '8px',
        background: `
            linear-gradient(90deg, 
                rgba(255, 255, 255, 0.05) 0%, 
                rgba(255, 255, 255, 0.02) 100%
            )
        `,
        borderRadius: '12px',
        cursor: 'pointer',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        boxShadow: `
            inset 0 2px 4px rgba(0, 0, 0, 0.2),
            0 1px 0 rgba(255, 255, 255, 0.05)
        `,
    },
    cosmicGlow: {
        position: 'absolute',
        height: '100%',
        background: `
            radial-gradient(ellipse at center, 
                rgba(147, 112, 219, 0.3) 0%, 
                rgba(147, 112, 219, 0.1) 50%, 
                transparent 100%
            )
        `,
        borderRadius: '12px',
        filter: 'blur(8px)',
        zIndex: 1,
    },
    progress: {
        position: 'absolute',
        height: '100%',
        borderRadius: '12px',
        zIndex: 2,
    },
    thumbContainer: {
        position: 'absolute',
        top: '50%',
        width: '20px',
        height: '20px',
        cursor: 'grab',
        zIndex: 3,
    },
    thumb: {
        width: '20px',
        height: '20px',
        background: 'rgba(147, 112, 219, 0.9)',
        borderRadius: '50%',
        border: '2px solid rgba(147, 112, 219, 0.8)',
        boxShadow: `
            0 0 20px rgba(147, 112, 219, 0.6),
            0 0 40px rgba(147, 112, 219, 0.3),
            0 4px 15px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.4)
        `,
        transition: 'all 0.2s ease',
        position: 'relative',
    },
    thumbNoGlow: {
        width: '20px',
        height: '20px',
        background: 'rgba(147, 112, 219, 0.9)',
        borderRadius: '50%',
        border: '2px solid rgba(147, 112, 219, 0.8)',
        boxShadow: `
            0 4px 15px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.4)
        `,
        transition: 'all 0.2s ease',
        position: 'relative',
    }
}; 